import { Button, Collapsible, Flex, Text } from '@chakra-ui/react';
import { formatPrice, formatVolume } from '@features/prediction-markets/fakeMarketData';
import { MarketOutcome } from '@features/prediction-markets/types';
import { memo, useCallback } from 'react';

export type YesNo = 'yes' | 'no';

interface Props {
  outcome: MarketOutcome;
  isExpanded: boolean;
  isSelectedOutcome: boolean;
  selectedYesNo: YesNo;
  onSelectOutcome: (outcome: MarketOutcome, yesNo: YesNo) => void;
}

const OutcomeRow = ({
  outcome,
  isExpanded,
  isSelectedOutcome,
  selectedYesNo,
  onSelectOutcome,
}: Props) => {
  const handleBuyYes = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelectOutcome(outcome, 'yes');
    },
    [outcome, onSelectOutcome],
  );

  const handleBuyNo = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelectOutcome(outcome, 'no');
    },
    [outcome, onSelectOutcome],
  );

  const yesIsActive = isSelectedOutcome && selectedYesNo === 'yes';
  const noIsActive = isSelectedOutcome && selectedYesNo === 'no';

  return (
    <Collapsible.Trigger asChild>
      <Flex
        direction={{ base: 'column', sm: 'row' }}
        alignItems={{ base: 'stretch', sm: 'center' }}
        gap={{ base: 2, sm: 4 }}
        px={4}
        py={3}
        bg={isExpanded ? 'surface.accent' : 'surface.muted'}
        _hover={{ bg: 'surface.hover' }}
        cursor="pointer"
        transition="background 0.15s"
        borderRadius="lg"
      >
        <Flex alignItems="center" gap={2} flex={1} minW={0}>
          <Flex direction="column" flex={1} minW={0}>
            <Text fontSize="sm" fontWeight="semibold" color="text.primary">
              {outcome.label}
            </Text>
            <Text fontSize="xs" color="text.secondary">
              {formatVolume(outcome.volume)} Vol.
            </Text>
          </Flex>

          <Text
            fontSize="sm"
            fontWeight="medium"
            color="text.secondary"
            flexShrink={0}
            minW="50px"
            textAlign="end"
          >
            {outcome.probability < 1 ? '<1%' : `${outcome.probability}%`}
          </Text>
        </Flex>

        <Flex gap={2} flexShrink={0}>
          <Button
            size="xs"
            flex={{ base: 1, sm: 'initial' }}
            bg={yesIsActive ? 'green.500' : 'green.100'}
            color={yesIsActive ? 'white' : 'green.600'}
            _hover={{ bg: 'green.500', color: 'white' }}
            _dark={{
              bg: yesIsActive ? 'green.500' : 'green.900/30',
              color: yesIsActive ? 'white' : 'green.300',
              _hover: { bg: 'green.500', color: 'white' },
            }}
            borderRadius="lg"
            onClick={handleBuyYes}
          >
            Buy Yes {formatPrice(outcome.yesPrice)}
          </Button>
          <Button
            size="xs"
            flex={{ base: 1, sm: 'initial' }}
            bg={noIsActive ? 'red.500' : 'red.100'}
            color={noIsActive ? 'white' : 'red.600'}
            _hover={{ bg: 'red.500', color: 'white' }}
            _dark={{
              bg: noIsActive ? 'red.500' : 'red.900/30',
              color: noIsActive ? 'white' : 'red.300',
              _hover: { bg: 'red.500', color: 'white' },
            }}
            borderRadius="lg"
            onClick={handleBuyNo}
          >
            Buy No {formatPrice(outcome.noPrice)}
          </Button>
        </Flex>
      </Flex>
    </Collapsible.Trigger>
  );
};

export default memo(OutcomeRow);
